/**
 * Remote Test Command
 *
 * Triggers workflow tests on the Canary platform and streams results via SSE.
 * Designed for CI/CD pipelines - exits with code 0 on success, 1 on failure.
 *
 * Usage:
 *   canary test-suite run [options]
 *
 * Options:
 *   --token <key>         API key (or set CANARY_API_TOKEN env var)
 *   --api-url <url>       API URL (default: https://api.runcofactor.com)
 *   --tag <tag>           Filter workflows by tag
 *   --name-pattern <pat>  Filter workflows by name pattern
 *   --verbose, -v         Show all events (not just results)
 */

import process from 'node:process';
import { createParser, type EventSourceMessage } from 'eventsource-parser';
import { resolveConfig } from './auth';
import { resolveTestConfig, UUID_RE, type ResolvedConfig } from './remote-test-config';
import {
  buildQueryParams,
  extractWorkflowName,
  formatFailedTests,
  formatExternalRunSummary,
  formatStreamClosedBeforeCompletion,
  formatStreamReadFailure,
  formatSummary,
  getArgValue,
  hasFlag,
  type FailedTest,
} from './remote-test-format';
import { isTriageEnabledForExecution } from './triage.js';

type WorkflowTestStatus = 'queued' | 'running' | 'waiting' | 'success' | 'failed';

type WorkflowTestEvent = {
  type: 'workflow-test';
  suiteId: string;
  workflowId: string;
  testRunId: string;
  workflowRunId: string | null;
  status: WorkflowTestStatus;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  errorMessage?: string | null;
  linkedIssueId?: string | null;
  message?: string;
  resumeAt?: string | null;
  segmentIndex?: number | null;
  totalSegments?: number | null;
};

type WorkflowTestSuiteEvent = {
  type: 'workflow-test-suite';
  suiteId: string;
  status: 'running' | 'completed';
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  totalWorkflows: number;
  completedWorkflows: number;
  failedWorkflows: number;
  successfulWorkflows: number;
  errorMessage?: string | null;
  message?: string;
};

type TriggerResponse = {
  ok: boolean;
  jobId?: string;
  suiteId?: string;
  startedAt?: string;
  appUrl?: string;
  error?: string;
};

// Export internals for testing
export const __internals = {
  getArgValue,
  hasFlag,
  buildQueryParams,
  extractWorkflowName,
  formatSummary,
  formatExternalRunSummary,
  formatFailedTests,
  formatStreamReadFailure,
  formatStreamClosedBeforeCompletion,
  resolveTestConfig,
  UUID_RE,
};

type SuiteListItem = { id: string; name: string };
type NamedSuiteTrigger =
  | { kind: 'native'; executionId: string; jobId: string }
  | { kind: 'external'; runId: string; jobId: string; runUrl: string };

/**
 * Resolve a `--suite <name|id>` argument to a suite id and trigger a layered
 * run via POST /test-suites/:id/run. Returns the execution id (used as the SSE
 * stream key) and the orchestrator job id.
 */
async function triggerNamedSuiteRun(
  apiUrl: string,
  token: string,
  suiteArg: string,
  environmentId: string | undefined,
  environmentMode: 'mixed' | 'force',
  verbose: boolean,
  externalSelection?: { specPath?: string; grep?: string }
): Promise<NamedSuiteTrigger> {
  const authHeaders = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  // Resolve name → id (exact id is accepted as-is; otherwise match by name).
  const listRes = await fetch(`${apiUrl}/test-suites`, { headers: authHeaders });
  if (!listRes.ok) {
    console.error(`Failed to list test suites: ${listRes.status}`);
    process.exit(1);
  }
  const listJson = (await listRes.json()) as { ok: boolean; data?: SuiteListItem[] };
  const suites = listJson.data ?? [];
  const match =
    suites.find((s) => s.id === suiteArg) ??
    suites.find((s) => s.name.toLowerCase() === suiteArg.toLowerCase());
  if (!match) {
    console.error(`Suite not found: "${suiteArg}"`);
    if (suites.length > 0) {
      console.error('Available suites:');
      for (const s of suites) console.error(`  - ${s.name} (${s.id})`);
    }
    process.exit(1);
  }

  console.log(`Starting test suite: ${match.name}`);
  console.log(`  Env mode: ${environmentMode}`);
  if (environmentMode === 'force' && environmentId) console.log(`  Environment: ${environmentId}`);
  console.log('');

  const runBody: {
    environmentId?: string;
    environmentMode: 'mixed' | 'force';
    externalSelection?: { specPath?: string; grep?: string };
  } = {
    environmentMode,
  };
  if (environmentMode === 'force' && environmentId) runBody.environmentId = environmentId;
  if (externalSelection) runBody.externalSelection = externalSelection;
  const runRes = await fetch(`${apiUrl}/test-suites/${match.id}/run`, {
    method: 'POST',
    headers: authHeaders,
    body: JSON.stringify(runBody),
  });
  if (!runRes.ok) {
    console.error(`Failed to start suite: ${runRes.status}`);
    console.error(await runRes.text());
    process.exit(1);
  }
  const runJson = (await runRes.json()) as {
    ok: boolean;
    data?: NamedSuiteTrigger;
    error?: string;
  };
  if (!runJson.ok || !runJson.data) {
    console.error(`Failed to start suite: ${runJson.error ?? 'Unknown error'}`);
    process.exit(1);
  }
  if (verbose) {
    console.log(
      runJson.data.kind === 'external'
        ? `External run ID: ${runJson.data.runId}`
        : `Execution ID: ${runJson.data.executionId}`
    );
    console.log(`Job ID: ${runJson.data.jobId}`);
    console.log('');
  }
  return runJson.data;
}

export async function streamExternalRun(
  apiUrl: string,
  token: string,
  run: Extract<NamedSuiteTrigger, { kind: 'external' }>,
  verbose: boolean
): Promise<never> {
  const response = await fetch(`${apiUrl}/jobs/${run.runId}/stream`, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
  });
  if (!response.ok || !response.body) {
    console.error(`Failed to connect to external run stream: ${response.status}`);
    process.exit(1);
  }

  let sealed = false;
  let exitCode = 1;
  const parser = createParser({
    onEvent: (event: EventSourceMessage) => {
      if (!event.data || !event.event?.startsWith('external-run.')) return;
      try {
        const data = JSON.parse(event.data) as Record<string, unknown>;
        if (verbose) console.log(`[${event.event}] ${JSON.stringify(data)}`);
        if (event.event === 'external-run.shard' && !verbose) {
          console.log(
            `  shard ${String(data.shardIndex)}/${String(data.shardTotal)}: ${String(data.phase)} (attempt ${String(data.attempt)})`
          );
        }
        if (event.event === 'external-run.status' && data.status === 'canceled') {
          sealed = true;
          exitCode = 1;
          console.error('External test run was canceled.');
        }
        if (event.event === 'external-run.sealed') {
          sealed = true;
          const total = Number(data.total ?? 0);
          const passed = Number(data.passed ?? 0);
          const failed = Number(data.failed ?? 0);
          const flaky = Number(data.flaky ?? 0);
          const skipped = Number(data.skipped ?? 0);
          const summary = formatExternalRunSummary({
            total,
            passed,
            failed,
            flaky,
            skipped,
            resultsTruncated: data.resultsTruncated === true,
          });
          console.log('');
          console.log('─'.repeat(50));
          console.log(summary.message);
          console.log(`Run: ${apiUrl.replace(/\/api\/?$/, '')}${run.runUrl}`);
          exitCode = summary.exitCode;
        }
      } catch (error) {
        if (verbose) console.error(`Ignored malformed stream event: ${String(error)}`);
      }
    },
  });
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  try {
    while (!sealed) {
      const { done, value } = await reader.read();
      if (done) break;
      parser.feed(decoder.decode(value, { stream: true }));
    }
  } catch (error) {
    console.error(formatStreamReadFailure(error));
  } finally {
    reader.releaseLock();
  }
  if (!sealed) console.error(formatStreamClosedBeforeCompletion());
  process.exit(exitCode);
}

export async function runRemoteTest(argv: string[]): Promise<void> {
  const { apiUrl, token } = await resolveConfig(argv);

  const tag = getArgValue(argv, '--tag');
  const namePattern = getArgValue(argv, '--name-pattern');
  // "--application" is the user-facing flag; "--property" stays as a backwards-compat alias.
  const propertyArg = getArgValue(argv, '--application') ?? getArgValue(argv, '--property');
  const environmentArg = getArgValue(argv, '--environment');
  const suiteArg = getArgValue(argv, '--suite');
  const externalSpec = getArgValue(argv, '--external-spec');
  const externalGrep = getArgValue(argv, '--external-grep');
  const verbose = hasFlag(argv, '--verbose', '-v');
  if ((externalSpec || externalGrep) && !suiteArg) {
    console.error('Error: --external-spec and --external-grep require --suite <name|id>.');
    process.exit(1);
  }

  // Env mode: 'mixed' (default) runs each flow on its own preferred env; 'force'
  // pins the whole suite to --environment (which then becomes required).
  const envModeArg = getArgValue(argv, '--env-mode');
  if (envModeArg !== undefined && envModeArg !== 'mixed' && envModeArg !== 'force') {
    console.error(`Error: --env-mode must be "mixed" or "force" (got "${envModeArg}").`);
    process.exit(1);
  }
  const environmentMode: 'mixed' | 'force' =
    (envModeArg as 'mixed' | 'force' | undefined) ?? (environmentArg ? 'force' : 'mixed');
  if (environmentMode === 'force' && !environmentArg) {
    console.error('Error: --env-mode force requires --environment <name|id>.');
    process.exit(1);
  }

  // 0. Resolve property/environment if specified
  let resolvedConfig: ResolvedConfig = {};
  if (propertyArg || environmentArg) {
    try {
      resolvedConfig = await resolveTestConfig(apiUrl, token, propertyArg, environmentArg);
    } catch (err) {
      console.error(`${err instanceof Error ? err.message : String(err)}`);
      process.exit(1);
    }
  }

  console.log('Starting remote workflow tests...');
  if (resolvedConfig.propertyId) console.log(`  Application: ${propertyArg}`);
  if (resolvedConfig.environmentId) console.log(`  Environment: ${environmentArg}`);
  if (tag) console.log(`  Filtering by tag: ${tag}`);
  if (namePattern) console.log(`  Filtering by name pattern: ${namePattern}`);
  console.log('');

  let suiteId: string;
  let jobId: string | undefined;
  let appUrl: string | undefined;

  if (suiteArg) {
    // Named, layered suite run. Resolve name→id, then POST /test-suites/:id/run.
    const triggered = await triggerNamedSuiteRun(
      apiUrl,
      token,
      suiteArg,
      resolvedConfig.environmentId,
      environmentMode,
      verbose,
      externalSpec || externalGrep
        ? { ...(externalSpec ? { specPath: externalSpec } : {}), ...(externalGrep ? { grep: externalGrep } : {}) }
        : undefined
    );
    if (triggered.kind === 'external') {
      return await streamExternalRun(apiUrl, token, triggered, verbose);
    }
    suiteId = triggered.executionId;
    jobId = triggered.jobId;
  } else {
    console.log('Starting remote workflow tests...');
    if (resolvedConfig.propertyId) console.log(`  Property: ${propertyArg}`);
    if (resolvedConfig.environmentId) console.log(`  Environment: ${environmentArg}`);
    if (tag) console.log(`  Filtering by tag: ${tag}`);
    if (namePattern) console.log(`  Filtering by name pattern: ${namePattern}`);
    console.log('');

    // 1. Trigger test run
    const queryParams = new URLSearchParams();
    if (tag) queryParams.set('tag', tag);
    if (namePattern) queryParams.set('namePattern', namePattern);

    const triggerUrl = `${apiUrl}/workflows/test-runs${queryParams.toString() ? `?${queryParams}` : ''}`;

    const body: Record<string, string> = { environmentMode };
    if (resolvedConfig.propertyId) body.propertyId = resolvedConfig.propertyId;
    if (environmentMode === 'force' && resolvedConfig.environmentId)
      body.environmentId = resolvedConfig.environmentId;

    let triggerRes: Response;
    try {
      triggerRes = await fetch(triggerUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
    } catch (err) {
      console.error(`Failed to connect to API: ${err}`);
      process.exit(1);
    }

    if (!triggerRes.ok) {
      const errorText = await triggerRes.text();
      console.error(`Failed to start tests: ${triggerRes.status}`);
      console.error(errorText);
      process.exit(1);
    }

    const triggerData = (await triggerRes.json()) as TriggerResponse;
    if (!triggerData.ok || !triggerData.suiteId) {
      console.error(`Failed to start tests: ${triggerData.error ?? 'Unknown error'}`);
      process.exit(1);
    }
    suiteId = triggerData.suiteId;
    jobId = triggerData.jobId;
    appUrl = triggerData.appUrl;
  }

  if (verbose) {
    console.log(`Suite ID: ${suiteId}`);
    console.log(`Job ID: ${jobId}`);
    console.log('');
  }

  // 2. Subscribe to SSE stream
  const streamUrl = `${apiUrl}/workflows/test-runs/stream?suiteId=${suiteId}`;

  let streamRes: Response;
  try {
    streamRes = await fetch(streamUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
      },
    });
  } catch (err) {
    console.error(`Failed to connect to event stream: ${err}`);
    process.exit(1);
  }

  if (!streamRes.ok || !streamRes.body) {
    console.error(`Failed to connect to event stream: ${streamRes.status}`);
    process.exit(1);
  }

  // Track state
  let exitCode = 0;
  let hasCompleted = false;
  const workflowNames = new Map<string, string>();
  const failedTests: FailedTest[] = [];
  let totalWorkflows = 0;
  let completedWorkflows = 0;
  let failedWorkflows = 0;
  let successfulWorkflows = 0;
  let streamInterrupted = false;

  // 3. Parse SSE events
  const parser = createParser({
    onEvent: (event: EventSourceMessage) => {
      if (!event.data) return;

      try {
        const data = JSON.parse(event.data) as
          | WorkflowTestEvent
          | WorkflowTestSuiteEvent
          | { error?: string };

        if (verbose) {
          console.log(`[${event.event}] ${JSON.stringify(data)}`);
        }

        if (event.event === 'workflow-test') {
          const testEvent = data as WorkflowTestEvent;
          const { status, workflowId, message, errorMessage } = testEvent;
          const name =
            workflowNames.get(workflowId) ||
            message?.replace(/^Flow "(.+)" .*$/, '$1') ||
            workflowId;

          if (message?.startsWith('Flow "')) {
            const match = message.match(/^Flow "(.+?)" /);
            if (match) workflowNames.set(workflowId, match[1]);
          }

          if (!verbose) {
            if (status === 'success') {
              console.log(`  \u2713 ${name}`);
            } else if (status === 'failed') {
              console.log(`  \u2717 ${name}`);
              if (errorMessage) {
                console.log(`    Error: ${errorMessage.slice(0, 200)}`);
              }
              failedTests.push({ name, testRunId: testEvent.testRunId });
              exitCode = 1;
            } else if (status === 'running') {
              // Don't log running status in non-verbose mode
            } else if (status === 'waiting') {
              console.log(`  \u23F3 ${name} (waiting for scheduled time)`);
            }
          }
        }

        if (event.event === 'workflow-test-suite') {
          const suiteEvent = data as WorkflowTestSuiteEvent;
          totalWorkflows = suiteEvent.totalWorkflows;
          completedWorkflows = suiteEvent.completedWorkflows;
          failedWorkflows = suiteEvent.failedWorkflows;
          successfulWorkflows = suiteEvent.successfulWorkflows;

          if (suiteEvent.status === 'completed') {
            hasCompleted = true;
          }
        }

        if (event.event === 'error') {
          const errorData = data as { error?: string };
          console.error(`Stream error: ${errorData.error ?? 'Unknown error'}`);
          exitCode = 1;
        }
      } catch {
        // Ignore parse errors for keepalive, etc.
      }
    },
  });

  const reader = streamRes.body.getReader();
  const decoder = new TextDecoder();

  try {
    while (!hasCompleted) {
      const { done, value } = await reader.read();
      if (done) {
        if (!hasCompleted) {
          streamInterrupted = true;
          exitCode = 1;
          console.error(formatStreamClosedBeforeCompletion());
        }
        break;
      }
      parser.feed(decoder.decode(value, { stream: true }));
    }
  } catch (err) {
    streamInterrupted = true;
    exitCode = 1;
    console.error(formatStreamReadFailure(err));
  } finally {
    reader.releaseLock();
  }

  // 4. Print summary
  console.log('');
  console.log('─'.repeat(50));

  if (totalWorkflows === 0) {
    console.log('No workflows found matching the filter criteria.');
    process.exit(streamInterrupted ? 1 : 0);
  }

  const passRate =
    totalWorkflows > 0 ? Math.round((successfulWorkflows / totalWorkflows) * 100) : 0;

  if (failedWorkflows > 0) {
    console.log(
      `FAILED: ${failedWorkflows} of ${totalWorkflows} workflows failed (${passRate}% pass rate)`
    );
    exitCode = 1;
  } else {
    console.log(`PASSED: ${successfulWorkflows} of ${totalWorkflows} workflows passed`);
    if (!streamInterrupted) {
      exitCode = 0;
    }
  }

  const waitingWorkflows = totalWorkflows - completedWorkflows;
  if (waitingWorkflows > 0) {
    console.log(`Note: ${waitingWorkflows} workflow(s) are still waiting (scheduled for later)`);
  }

  const failedSection = formatFailedTests(failedTests, appUrl);
  if (failedSection) {
    console.log(failedSection);
  }

  if (failedWorkflows > 0) {
    const triageEnabled = await isTriageEnabledForExecution(apiUrl, token, suiteId);
    if (triageEnabled) {
      console.log('');
      console.log(`Triage: canary triage wait ${suiteId}`);
    }
  }

  process.exit(exitCode);
}
